import type {FC} from 'react';

import {useI18n} from '@yourcoach/i18n';

import type {Props} from './types';

import {platform} from '../../../../utils/platform';
import * as S from './styles';

export const ChatConferenceBanner: FC<Props> = ({
  isActionButtonLoading,
  isForCoach = true,
  isForCorporateCourse = false,
  isTimeBeforeEndCritical = false,
  onActionButtonClick,
  startTime,
  timeBeforeEnd,
  type,
  ...rest
}) => {
  const {t} = useI18n();

  if (
    (['inProgress', 'overdue'] as Props['type'][]).includes(type) &&
    !isForCoach
  ) {
    return null;
  }

  const getTitleText = () => {
    switch (type) {
      case 'start':
        return t('shared:upcoming_chat_session_time', {
          time: startTime,
        });
      default:
        return '';
    }
  };

  const getText = () => {
    switch (type) {
      case 'inProgress':
        return t('shared:chat_session_in_progress');
      case 'overdue':
        return t('shared:dont_forget_to_end_session');
      case 'start': {
        if (!isForCoach) {
          return '';
        }

        let text = t('shared:press_start_conference_when_you_ready');

        if (isForCorporateCourse) {
          text += ` ${t('shared:you_must_start_chat_session_to_get_paid')}`;
        }

        return text;
      }
      default:
        return '';
    }
  };

  const getActionButtonText = () => {
    switch (type) {
      case 'inProgress':
      case 'overdue':
        return t('shared:end_session');
      case 'start':
        return t('shared:start');
      default:
        return '';
    }
  };

  const titleText = getTitleText();
  const text = getText();

  const renderTimeBeforeEnd = () => {
    if (!timeBeforeEnd) {
      return null;
    }

    return (
      <S.TimeBeforeEndContainer>
        <S.TimeBeforeEndIcon isCritical={isTimeBeforeEndCritical} />
        <S.TimeBeforeEndText isCritical={isTimeBeforeEndCritical} type={type}>
          {timeBeforeEnd}
        </S.TimeBeforeEndText>
      </S.TimeBeforeEndContainer>
    );
  };

  return (
    <S.Container isForCoach={isForCoach} type={type} {...rest}>
      {platform.isWeb && type === 'start' ? <S.ChatIcon /> : null}
      <S.Content>
        {titleText ? <S.Title type={type}>{titleText}</S.Title> : null}
        {platform.isNative && text && timeBeforeEnd ? (
          <S.SessionInfoRow>
            <S.Text type={type}>{text}</S.Text>
            {renderTimeBeforeEnd()}
          </S.SessionInfoRow>
        ) : (
          <>
            {text ? <S.Text type={type}>{text}</S.Text> : null}
            {renderTimeBeforeEnd()}
          </>
        )}
      </S.Content>
      {isForCoach ? (
        <S.ActionButton
          isLoading={isActionButtonLoading}
          onClick={onActionButtonClick}
          type={type}
        >
          {!isActionButtonLoading ? <S.ActionButtonIcon type={type} /> : null}
          <S.ActionButtonText type={type}>
            {getActionButtonText()}
          </S.ActionButtonText>
        </S.ActionButton>
      ) : null}
    </S.Container>
  );
};
